import { useMemo } from 'react';

import { Icon } from '@/components/Icon';
import { Indicator } from '@/components/Indicator/Indicator';

import { mergeClassNames } from '@/utils';
import { getBatteryZone } from '@/utils/getBatteryZone';

import { useSensorsContext } from '@/contexts/SensorsContext';

import styles from './SensorHubIndicator.module.css';

interface SensorHubIndicatorProps {
  className?: string;
  title?: string;
}

export const SensorHubIndicator = ({ title = 'Sensor Hub', ...props }: SensorHubIndicatorProps) => {
  const { hubStatus, hubBatteryLevel } = useSensorsContext();

  const batteryZone = useMemo(() => getBatteryZone(hubBatteryLevel), [hubBatteryLevel]);

  const hubStatusText = useMemo(() => {
    switch (hubStatus) {
      case 'disconnected':
        return 'Disconnected';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      default:
        return 'Unknown';
    }
  }, [hubStatus]);

  return (
    <div className={styles.container} {...props}>
      <span className={styles.title}>{title}</span>

      {hubStatus && hubBatteryLevel === undefined && (
        <div className={mergeClassNames(styles.status, styles[hubStatus])}>
          <Indicator color='currentColor' />
          {hubStatusText}
        </div>
      )}

      {hubStatus === 'connected' && hubBatteryLevel !== undefined && (
        <div className={mergeClassNames(styles.batteryIndicator, styles[batteryZone])}>
          <Icon
            name={
              {
                high: 'batteryFull' as const,
                medium: 'batteryWarning' as const,
                low: 'batteryCritical' as const,
              }[batteryZone]
            }
          />
          {hubBatteryLevel}%
        </div>
      )}
    </div>
  );
};
